import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { GetSalesInsightsGroupedRequest, SalesInsightsCompareGroupedResponse, useSalesInsightsCompareGrouped } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { UserContext } from '../../../../context/UserContext';
import { dateRange, formatDateTimeParam, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import ComparedPieBarChart, { ComparedPieBarChartData } from '../comparedCharts/ComparedPieBarChart';
import { TableChip } from '../../../../components/EnhancedTableToolbar';

const ComparedSalesByLocationPieBar = () =>{
    const { t } = useTranslation();
    const { locale } = useContext(UserContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig } = useContext(DashboardContext);
    
    const [ params, setParams ] = useState<GetSalesInsightsGroupedRequest>({
        groupBy: 'location',
        fields: 'totalSales',
        startDateTime: formatDateTimeParam(filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
        compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
    });

    const {
        data: salesInsightsGrouped,
        isLoading,
        isError,
        refetch: fetchComparedSalesByLocation
    } = useSalesInsightsCompareGrouped<SalesInsightsCompareGroupedResponse>(params, 'sales-insights-grouped-compare-sales-location');
        
    function transformData(data: SalesInsightsCompareGroupedResponse | undefined): ComparedPieBarChartData[] {
        // no data
        if (!data || (!data?.salesInsightsGrouped && !data?.comparedSalesInsightsGrouped)) return [];

        // no comparison data
        if (!data.comparedSalesInsightsGrouped) {
            return data.salesInsightsGrouped.map(item => ({
                label: item.key,
                value: item.salesInsights.totalSalesIncTax ?? 0,
                comparedValue: 0
            }));
        }

        // no current data
        if (!data.salesInsightsGrouped) {
            return data.comparedSalesInsightsGrouped.map(item => ({
                label: item.key,
                value: 0,
                comparedValue: item.salesInsights.totalSalesIncTax ?? 0
            }));
        }

        // both periods have data
        const currentDataMap = new Map(
            data.salesInsightsGrouped.map(item => [item.key, item.salesInsights.totalSalesIncTax])
        );
        
        const comparedDataMap = new Map(
            data.comparedSalesInsightsGrouped.map(item => [item.key, item.salesInsights.totalSalesIncTax])
        );
        
        const allKeys = new Set([
            ...(data.salesInsightsGrouped?.map(item => item.key) || []),
            ...(data.comparedSalesInsightsGrouped?.map(item => item.key) || [])
        ]);
        
        return Array.from(allKeys).map(key => ({
            label: key,
            value: currentDataMap.get(key) ?? 0,
            comparedValue: comparedDataMap.get(key) ?? 0,
        }));
    }

    useEffect(() => {
        setParams(p => ({
            ...p,
            startDateTime: formatDateTimeParam(filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            endDateTime: formatDateTimeParam(filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
            compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
            compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id,
        }))
        fetchComparedSalesByLocation();
    }, [filters, dateRangeConfig, companyConfig, fetchComparedSalesByLocation]);
        
    useEffect(() => {fetchComparedSalesByLocation();}, [params, fetchComparedSalesByLocation]);
    const currentChip: TableChip = useMemo(() => 
            ({
                label: `${formatPeriodLabel(
                    new Date(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                    locale
                  )} - ${formatPeriodLabel(new Date(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                  labelType: 'period'
            }), [filters.periodFrom, filters.periodTo]);
    
            const compareChip: TableChip = useMemo(() => 
            ({
                label: `${formatPeriodLabel(
                    new Date(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                    locale
                  )} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                  labelType: 'periodCompare'
            }), [filters.comparePeriodFrom, filters.comparePeriodTo]);

    return (
        <Box>
            <ComparedPieBarChart
                title={t("screens.dashboarding.charts.salesByLocation")}
                description={t("screens.dashboarding.charts.includingTax")}
                data={transformData(salesInsightsGrouped)}
                currency={salesInsightsGrouped?.currency}
                pieLabels={[
                    `${formatPeriodLabel(new Date(filters.periodFrom ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.periodTo ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                    `${formatPeriodLabel(new Date(filters.comparePeriodFrom ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`
                ]}
                locale={locale}
                isLoading={isLoading}
                error={isError}
                height={560}
                reportName={t("screens.dashboarding.charts.salesOverview")}
                reportLink={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByLocation.aspx`}
                chips={[currentChip, compareChip]}
            />
        </Box>
    )
}

export default ComparedSalesByLocationPieBar;
